import { TElement } from "@udecode/plate-common";
import { addMinutes } from "date-fns";
import { useCallback, useId, useMemo, useState } from "react";
import { toast } from "sonner";
import { useMutation } from "urql";

import {
    ControlledCheckboxInput,
    ControlledSelectInput,
    ControlledTextInput,
} from "@/components/FormInputs";
import { DateTimeInput } from "@/components/inputs/DateTimeInput";
import { ImageUploadInput } from "@/components/inputs/ImageInput";
import { PollInput, PollT } from "@/components/inputs/PollInput/PollInput";
import { VideoInput, VideoUploadState } from "@/components/inputs/VideoInput";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { AddPollInput } from "@/gen/graphql";
import { GameMetadata } from "@/gen/realtime";
import { useFormFields } from "@/hooks/useForm";
import { useViewerContext } from "@/hooks/useViewerContext";
import { useAllFeedIconOptions } from "@/routes/game-feed/hooks/useAllFeedIconOptions";
import { addFeedRecordMutation, useAddPoll } from "@/routes/game-feed/query";
import { cn } from "@/utils";
import { reportError } from "@/utils/errors";
import { useLanguageCodeWithDefault } from "@/utils/language";

import FeedRichTextEditor from "./FeedRichTextEditor";

export const LiveCommentaryForm = ({ game }: { game: GameMetadata }) => {
    const { viewer } = useViewerContext();
    const languageCode = useLanguageCodeWithDefault();
    const [mode, setMode] = useState<"misc" | "move" | "poll" | "twitter" | "youtube">("misc");
    const [{ error: _errorAddPoll }, addPoll] = useAddPoll();

    const form = useFormFields<{
        timestamp: Date;
        content: TElement[] | null;
        highlight: boolean;
        bgColor: string | null;
        textColor: string | null;
        twitterUrl: string | null;
        youtubeUrl: string | null;
        imageId: string | null;
        iconImageId: string | null;
        moveIndex: string | null;
        poll: PollT | null;
        videoUploadState: VideoUploadState | null;
    }>({
        timestamp: new Date(),
        content: null,
        highlight: false,
        bgColor: null,
        textColor: null,
        twitterUrl: null,
        youtubeUrl: null,
        imageId: null,
        iconImageId: null,
        moveIndex: null,
        poll: null,
        videoUploadState: null,
    });

    const [{ error: _errorAddRecord }, addFeedRecord] = useMutation(addFeedRecordMutation);

    const isSubmittable = useMemo(() => {
        return Boolean(
            form.values.content ||
                form.values.twitterUrl ||
                form.values.youtubeUrl ||
                form.values.imageId ||
                form.values.poll ||
                form.values.videoUploadState?.videoId
        );
    }, [
        form.values.imageId,
        form.values.poll,
        form.values.content,
        form.values.twitterUrl,
        form.values.videoUploadState?.videoId,
        form.values.youtubeUrl,
    ]);

    const onSubmit = useCallback(async () => {
        if (
            form.values.videoUploadState?.videoId &&
            form.values.videoUploadState.progress !== 100
        ) {
            toast.warning("Please wait for the video to finish uploading.");
            return;
        }

        if (!isSubmittable) {
            return;
        }

        let pollId = null;
        if (form.values.poll) {
            const addPollResult = await addPoll({
                input: form.values.poll,
            });

            if (addPollResult.error) {
                toast.error("Unable to add poll", { description: addPollResult.error.message });
                return;
            }

            pollId = addPollResult.data?.addPoll.id ?? null;
        }

        try {
            await addFeedRecord({
                input: {
                    gameId: game.id,
                    languageCode,
                    content: form.values.content,
                    timestamp: form.values.timestamp.toISOString(),
                    highlight: form.values.highlight,
                    bgColor: form.values.bgColor,
                    textColor: form.values.textColor,
                    twitterUrl: form.values.twitterUrl === "" ? null : form.values.twitterUrl,
                    youtubeUrl: form.values.youtubeUrl === "" ? null : form.values.youtubeUrl,
                    imageId: form.values.imageId,
                    iconImageId: form.values.iconImageId,
                    draft: false,
                    commentatorId: viewer?.commentator?.id,
                    pollId,
                    videoId: form.values.videoUploadState?.videoId,
                },
            });

            // TODO: Make form.reset() work for videos
            window.location.reload();
            // form.reset();
        } catch (e) {
            reportError(e, "Error adding record");
        }
    }, [
        isSubmittable,
        form,
        addPoll,
        addFeedRecord,
        game.id,
        languageCode,
        viewer?.commentator?.id,
    ]);

    const feedIconOptions = useAllFeedIconOptions();

    return (
        <div>
            <Card className="bg-brand-gray-light flex flex-col gap-4 rounded border-none p-4">
                <div className="flex flex-col gap-4">
                    <FeedEntryModeSelector mode={mode} setMode={setMode} />
                    {mode === "misc" ? (
                        <div className="flex flex-col gap-4">
                            <FeedRichTextEditor
                                initialValue={form.values.content}
                                onChange={value => form.setValue("content", value)}
                                game={game}
                            />
                            <ControlledSelectInput
                                className={"w-1/2"}
                                label={"Select icon"}
                                placeholder="No icon"
                                options={feedIconOptions}
                                onValue={value => {
                                    form.setValue("iconImageId", value);
                                }}
                                value={form.values.iconImageId}
                            />
                            <ImageUploadInput
                                className="w-1/4"
                                imageId={form.values.imageId}
                                label={"Upload image"}
                                onImageId={imageId => form.setValue("imageId", imageId)}
                            />
                            <VideoInput
                                className={"w-1/2"}
                                label="Upload video"
                                value={
                                    form.values.videoUploadState ?? { videoId: null, progress: 0 }
                                }
                                setValue={value => form.setValue("videoUploadState", value)}
                            />
                        </div>
                    ) : mode === "poll" ? (
                        <PollInput
                            poll={
                                form.values.poll
                                    ? form.values.poll
                                    : ({
                                          title: "",
                                          options: [],
                                          endAt: addMinutes(new Date(), 30).toISOString(),
                                      } as AddPollInput)
                            }
                            setPoll={poll => form.setValue("poll", poll)}
                        />
                    ) : mode === "twitter" ? (
                        <div className="my-4 flex flex-col gap-4 space-y-2">
                            <ControlledTextInput
                                value={form.values.twitterUrl ?? ""}
                                id="twitter"
                                onValue={twitterUrl => form.setValue("twitterUrl", twitterUrl)}
                                placeholder="Twitter URL"
                                type="url"
                                className="w-full"
                            />
                        </div>
                    ) : mode === "youtube" ? (
                        <div className="my-4 flex flex-col gap-4 space-y-2">
                            <ControlledTextInput
                                id="youtube"
                                value={form.values.youtubeUrl ?? ""}
                                onValue={youtubeUrl => form.setValue("youtubeUrl", youtubeUrl)}
                                placeholder="Youtube URL"
                                type="url"
                                className="w-full"
                            />
                        </div>
                    ) : null}
                </div>
                <ControlledCheckboxInput
                    checked={form.values.highlight}
                    label="Make highlight"
                    onValue={highlight => form.setValue("highlight", highlight)}
                />
                <DateTimeInput
                    value={form.values.timestamp}
                    onValue={date => form.setValue("timestamp", date ?? null)}
                    label="Timestamp"
                />
                <Button
                    onMouseDown={async event => {
                        event.preventDefault();
                        await onSubmit();
                    }}
                    disabled={!isSubmittable}
                    className="w-fit"
                >
                    Submit
                </Button>
            </Card>
            <Accordion
                type="single"
                collapsible
                className="text-brand-white hidden w-full md:block"
            >
                <AccordionItem value="item-1">
                    <AccordionTrigger>Keyboard shortcuts</AccordionTrigger>
                    <AccordionContent>
                        <Table>
                            <TableHeader>
                                <TableRow className="text-brand-white text-xs uppercase">
                                    <TableHead className="text-brand-white text-xs uppercase">
                                        Shortcut
                                    </TableHead>
                                    <TableHead className="text-brand-white text-xs uppercase">
                                        Value
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <TableRow className="border-none">
                                    <TableCell className="font-medium">cmd+H</TableCell>
                                    <TableCell className="font-medium">Heading</TableCell>
                                </TableRow>
                                <TableRow className="border-none">
                                    <TableCell className="font-medium">cmd+B</TableCell>
                                    <TableCell className="font-medium">Bold</TableCell>
                                </TableRow>
                                <TableRow className="border-none">
                                    <TableCell className="font-medium">cmd+ENTER</TableCell>
                                    <TableCell className="font-medium">Submit</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    );
};

export type FeedEntryMode = "misc" | "move" | "poll" | "twitter" | "youtube";

export const FeedEntryModeSelector = ({
    mode,
    setMode,
    className,
}: {
    mode: FeedEntryMode;
    setMode: (mode: FeedEntryMode) => void;
    className?: string;
}) => {
    const miscId = useId();
    const pollId = useId();
    const twitterId = useId();
    const youtubeId = useId();

    return (
        <RadioGroup
            className={cn("flex flex-row", className)}
            default-value="option-one"
            value={mode}
            onValueChange={value => {
                setMode(value as FeedEntryMode);
            }}
        >
            <div className="flex items-center space-x-2">
                <RadioGroupItem id={miscId} value="misc" />
                <Label htmlFor={miscId}>Misc</Label>
            </div>
            <div className="flex items-center space-x-2">
                <RadioGroupItem id={pollId} value="poll" />
                <Label htmlFor={pollId}>Poll</Label>
            </div>
            <div className="flex items-center space-x-2">
                <RadioGroupItem id={twitterId} value="twitter" />
                <Label htmlFor={twitterId}>Twitter</Label>
            </div>
            <div className="flex items-center space-x-2">
                <RadioGroupItem id={youtubeId} value="youtube" />
                <Label htmlFor={youtubeId}>Youtube</Label>
            </div>
        </RadioGroup>
    );
};
